import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Trash2, Archive } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/config/env";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getMenuUrl, hasValidTableSession, getTableInfo } from "@/utils/menu";
import {
  checkArqueoStatus,
  getLocalArqueoStatus,
  subscribeToArqueoChanges,
} from "@/utils/arqueoUtils";
import restoLogoPequeno from "@/assets/images/resto-logo-pequeño.png";

const VALID_STATES = [
  "En Carrito",
  // "En Curso",
  // "En Preparacion",
  // "Pendiente",
  // null,
  // undefined,
];

const OrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [menuUrl, setMenuUrl] = useState("/");
  const [arqueoActive, setArqueoActive] = useState(false);

  // Función para validar y obtener información de la mesa
  const validateSession = useCallback(async () => {
    try {
      console.log("validateSession ", localStorage.getItem("mesaId"));
      const mesaId = localStorage.getItem("mesaId");

      const tableInfo = getTableInfo();

      if (!mesaId || !tableInfo) {
        console.warn("[OrdersPage] Missing table information");
        throw new Error("Información de mesa no disponible");
      }

      const response = await axios.post(`${API_URL}/api/table/getTable`, {
        mesaId: mesaId,
      });

      if (!response.data || !response.data.table) {
        console.error("[OrdersPage] Invalid table response:", response.data);
        throw new Error("Datos de mesa inválidos");
      }

      return true;
    } catch (error) {
      console.error("[OrdersPage] Session validation error:", error);
      return false;
    }
  }, []);

  const calculateCartTotal = (items) => {
    return items.reduce((acc, item) => acc + Number(item.price), 0);
  };

  // Función para obtener datos del carrito
  const fetchCartData = useCallback(async () => {
    try {
      const mesaId = localStorage.getItem("mesaId");
      console.log("[OrdersPage] Fetching cart data for mesaId:", mesaId);

      if (!mesaId) {
        throw new Error("No se encontró ID de mesa");
      }

      const response = await axios.post(`${API_URL}/api/cart/get`, { mesaId });
      console.log("[OrdersPage] Cart response:", response.data);

      if (!response.data?.cartData) {
        throw new Error("Formato de respuesta inválido");
      }

      const validatedCartData = response.data.cartData
        .map((item) => ({
          ...item,
          price: Number(item.price) || 0,
        }))
        .filter((item) => VALID_STATES.includes(item.statusFood));

      setCartItems(validatedCartData);
      setError(null);
    } catch (err) {
      console.error("[OrdersPage] Error fetching cart data:", err);
      setError(err.message || "Error al cargar los datos del carrito");
      toast.error("Error al cargar los datos del pedido");
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar estado del arqueo
  const fetchArqueoStatus = useCallback(async () => {
    try {
      // Primero intentar obtener del localStorage para respuesta rápida
      const localStatus = getLocalArqueoStatus();
      setArqueoActive(localStatus);

      // Luego intentar obtener del servidor para asegurar consistencia
      const serverStatus = await checkArqueoStatus();
      setArqueoActive(serverStatus);

      console.log("[OrdersPage] Arqueo active:", arqueoActive);
    } catch (error) {
      console.error("[OrdersPage] Error fetching arqueo status:", error);
      // Mantener el estado local si hay error
    }
  }, []);

  // Inicialización y validación
  useEffect(() => {
    const init = async () => {
      try {
        const url = getMenuUrl();
        setMenuUrl(url);

        const isSessionValid = await validateSession();
        console.log("[OrdersPage] Session validation result:", isSessionValid);

        if (!isSessionValid) {
          console.warn("[OrdersPage] Invalid session, redirecting to menu");
          toast.error("Sesión de mesa no válida");
          navigate(url);
          return;
        }

        await Promise.all([fetchCartData(), fetchArqueoStatus()]);
      } catch (error) {
        console.error("[OrdersPage] Initialization error:", error);
        toast.error("Error al inicializar la página");
        navigate("/");
      }
    };

    init();

    // Suscribirse a cambios en el estado del arqueo
    const unsubscribe = subscribeToArqueoChanges((isActive) => {
      setArqueoActive(isActive);
      console.log("[OrdersPage] Arqueo state changed:", isActive);
    });

    return () => {
      unsubscribe(); // Limpiar suscripción al desmontar
    };
  }, [navigate, fetchCartData, validateSession, fetchArqueoStatus]);

  const handleRemoveItem = async (id) => {
    try {
      const mesaId = localStorage.getItem("mesaId");
      if (!mesaId) throw new Error("No se encontró el ID de la mesa");

      await axios.post(`${API_URL}/api/cart/remove`, {
        itemId: id,
        mesaId: mesaId,
      });

      setCartItems((prev) => prev.filter((item) => item._id !== id));
      toast.success("Producto eliminado del pedido");
    } catch (err) {
      console.error("Error removing item:", err);
      toast.error("Error al eliminar el producto");
    }
  };

  const handleCommentChange = (id, comment) => {
    setComments((prev) => ({
      ...prev,
      [id]: comment,
    }));
  };

  const handleSubmitOrder = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const mesaId = localStorage.getItem("mesaId");
      if (!mesaId) throw new Error("No se encontró el ID de la mesa");

      const total = calculateCartTotal(cartItems);

      // Formatear los items incluyendo el addOnSelected
      const formattedItems = cartItems.map((item) => ({
        productId: item._id,
        name: item.name,
        price: Number(item.price),
        quantity: item.quantity || 1,
        comment: comments[item._id] || "",
        destination: item.destination,
        selectedAddon: item.addOnSelected, // Incluir el addOnSelected
        status: item.statusFood || "En Preparacion",
      }));

      // Obtener estado actual del arqueo para incluirlo en la orden
      const currentArqueoStatus = arqueoActive;

      // Enviar pedido con los items formateados y el estado del arqueo
      const orderResponse = await axios.post(
        `${API_URL}/api/order/orderResto`,
        {
          mesaId,
          items: formattedItems,
          amount: total,
          status: "En Preparacion",
         // date: new Date(),
          payment: "",
          preferencies: "",
          isArqueoActive: currentArqueoStatus, // Usar el nombre correcto del campo
        }
      );

      // Guardar comentarios solo si la orden se creó exitosamente
      if (orderResponse.data?.success && Object.keys(comments).length > 0) {
        const commentsToSave = Object.entries(comments)
          .filter(([_, comment]) => comment.trim() !== "")
          .map(([itemId, comment]) => ({
            itemId,
            comment,
            orderId: orderResponse.data.orderId,
          }));

        if (commentsToSave.length > 0) {
          await axios.post(`${API_URL}/api/order/savePreferencies`, {
            mesaId,
            comments: commentsToSave,
          });
        }
      }

      toast.success("Pedido enviado correctamente");
      setOrderSubmitted(true);
      setCartItems([]);
      setComments({});
    } catch (err) {
      console.error("Error submitting order:", err);
      toast.error(err.message || "Error al enviar el pedido");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto" />
          <p className="mt-4 text-gray-600">Cargando pedido...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md p-4 flex justify-between items-center">
        <Link to={menuUrl} className="flex-shrink-0">
          <img src={restoLogoPequeno} alt="Logo" className="h-6 w-auto" />
        </Link>
        <nav className="flex gap-4">
          <Link
            to="/pedidos"
            className={`font-medium text-sm ${
              location.pathname === "/pedidos"
                ? "text-orange-500"
                : "text-gray-800 hover:text-gray-600"
            }`}
          >
            MIS PEDIDOS
          </Link>
          <Link
            to="/pagar"
            className="text-gray-800 hover:text-gray-600 font-medium text-sm"
          >
            PAGAR
          </Link>
        </nav>
      </header>

      <div className="mt-20 px-4 max-w-2xl mx-auto w-full pb-32">
        <div className="space-y-4">
          <div className="flex justify-between items-center border-b border-gray-200 pb-4">
            <h1 className="text-xs font-bold text-orange-500">MIS PEDIDOS</h1>
            <div className="flex items-center">
              {arqueoActive && (
                <div className="flex items-center mr-4 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  <Archive className="h-3 w-3 mr-1" />
                  {/* <span>Arqueo activo</span> */}
                </div>
              )}
              <Link
                to="/pagar"
                className="text-gray-800 hover:text-gray-600 font-medium text-sm"
              >
                PAGA TU ORDEN
              </Link>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-lg">{error}</div>
          )}

          {!error && (cartItems.length === 0 || orderSubmitted) ? (
            <div className="text-center py-12">
              {orderSubmitted ? (
                <>
                  <p className="text-gray-500 mb-4">
                    Tu pedido ha sido enviado correctamente
                  </p>
                  <p className="text-gray-500 mb-4">
                    Puedes proceder al pago o hacer otro pedido
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Button
                      onClick={() => navigate("/pagar")}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      Proceder al pago
                    </Button>
                    <Button
                      onClick={() => navigate(menuUrl)}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                      Pedir más
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-500">No hay pedidos en curso</p>
                  <Link
                    to={menuUrl}
                    className="mt-4 inline-block text-orange-500 hover:text-orange-600 font-medium"
                  >
                    Volver al menú
                  </Link>
                </>
              )}
            </div>
          ) : (
            <>
              {cartItems.map((item) => (
                <Card key={item._id} className="w-full">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h3 className="font-semibold text-lg">{item.name}</h3>
                        {item.addOnSelected && (
                          <p className="text-sm text-gray-600">
                            Opción: {item.addOnSelected}
                          </p>
                        )}
                        <p className="text-gray-600">
                          Precio: ${Number(item.price).toFixed(2)}
                        </p>
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                          {item.statusFood || "En Curso"}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveItem(item._id)}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                    <div className="mt-4">
                      <Input
                        placeholder="Comentarios..."
                        value={comments[item._id] || ""}
                        onChange={(e) =>
                          handleCommentChange(item._id, e.target.value)
                        }
                        className="w-full bg-gray-50"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}

              {cartItems.length > 0 && !orderSubmitted && (
                <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4">
                  <div className="max-w-2xl mx-auto">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xl font-bold">Total</span>
                      <span className="text-xl font-bold">
                        $
                        {cartItems
                          .reduce((acc, item) => acc + Number(item.price), 0)
                          .toFixed(2)}
                      </span>
                    </div>
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg text-lg font-semibold disabled:opacity-50"
                      onClick={handleSubmitOrder}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Enviando..." : "Enviar Pedido"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
